import { useRef, useState } from "react";
import { ImagePlus, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fileUrl } from "@/components/site/Services";

const MAX_SIDE = 1800;

const shrink = (file) => new Promise((resolve) => {
  if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return resolve(file);
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    URL.revokeObjectURL(url);
    const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
    if (scale === 1 && file.size < 900_000) return resolve(file);
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(img.width * scale);
    canvas.height = Math.round(img.height * scale);
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => resolve(blob ? new File([blob], file.name.replace(/\.\w+$/, ".webp"), { type: "image/webp" }) : file), "image/webp", 0.86);
  };
  img.onerror = () => { URL.revokeObjectURL(url); resolve(file); };
  img.src = url;
});

export const ImageUpload = ({ value, onChange, labels, testid, aspect = "aspect-[4/3]" }) => {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);

  const upload = async (e) => {
    const raw = e.target.files?.[0];
    e.target.value = "";
    if (!raw) return;
    setBusy(true);
    try {
      const file = await shrink(raw);
      const fd = new FormData();
      fd.append("file", file, file.name);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.path);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/gif,image/svg+xml" className="hidden" onChange={upload} data-testid={`${testid}-input`} />
      <button type="button" onClick={() => input.current?.click()} disabled={busy} data-testid={`${testid}-button`}
        className={`relative ${aspect} w-full overflow-hidden rounded-xl border border-dashed border-white/20 bg-white/[0.03] hover:border-sky-400/60 transition-colors grid place-items-center text-slate-400`}>
        {value && <img src={fileUrl(value)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
        <span className={`relative z-10 flex items-center gap-2 text-xs font-semibold ${value ? "rounded-full bg-[#050713]/70 backdrop-blur px-3 py-1.5 text-white" : ""}`}>
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImagePlus className="w-4 h-4" />}
          {busy ? labels.uploading : value ? labels.replace : labels.upload}
        </span>
      </button>
      {value && (
        <button type="button" onClick={() => onChange("")} data-testid={`${testid}-remove`} className="self-start flex items-center gap-1 text-xs text-rose-300 hover:text-rose-200">
          <Trash2 className="w-3.5 h-3.5" /> {labels.remove}
        </button>
      )}
    </div>
  );
};
